// D:\directiveai-react\frontend\src\components\home\CustomizeModal\CustomizeModal.js

import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  useMediaQuery,
  useTheme,
  Box,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Paper,
  Modal,
  IconButton,
  Checkbox
} from '@mui/material';
import { styled } from '@mui/material/styles';
import {
  Check,
  ChevronDown,
  Home
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { INTERFACE_STYLES } from '../HomeInput/styles';
import { saveUserSelectedApps } from '../services/AGTHallService';
import { ReactComponent as BrainIcon } from '../../../assets/images/loading-white.svg';

// Gradientes específicos para cada AGT
const CHAT_GRADIENT_LIGHT = 'linear-gradient(to bottom, #415B7E, #2D476A, #193356)';
const UPS_GRADIENT_LIGHT = 'linear-gradient(to bottom, #DC9844, #C88430, #B4701C)';
const JOB_GRADIENT_LIGHT = 'linear-gradient(to bottom, #4A4A4A, #333333, #1A1A1A)';
const FIT_GRADIENT_LIGHT = 'linear-gradient(to bottom, #FF8C00, #FF6B00, #FF5722)';
const HEALTH_GRADIENT_LIGHT = 'linear-gradient(to bottom, #4CAF50, #3d8b40, #2e7031)';

const CHAT_GRADIENT_DARK = 'linear-gradient(to bottom, #2A3D54, #1E2F45, #0F1A2B)';
const UPS_GRADIENT_DARK = 'linear-gradient(to bottom, #8A5F2A, #7A501D, #6B4312)';
const JOB_GRADIENT_DARK = 'linear-gradient(to bottom, #2D2D2D, #1F1F1F, #0F0F0F)';
const FIT_GRADIENT_DARK = 'linear-gradient(to bottom, #994F00, #994000, #993314)';
const HEALTH_GRADIENT_DARK = 'linear-gradient(to bottom, #2D6930, #245326, #1B421D)';

// Componente BrainIcon con gradiente
const BrainIconWithGradient = ({ gradient, gradientDark, appId }) => {
  const theme = useTheme();
  const isWallpaperMode = theme.palette.actualMode === 'wallpaper';
  const isDarkMode = theme.palette.mode === 'dark';
  const gradientId = `brain-gradient-customize-${appId}`;

  if (!gradient) {
    return <BrainIcon />;
  }

  const extractColors = (gradientString) => {
    const colorRegex = /#[0-9A-Fa-f]{6}/g;
    return gradientString.match(colorRegex) || [];
  };

  const invertColor = (hex) => {
    hex = hex.replace('#', '');
    const r = parseInt(hex.substring(0, 2), 16);
    const g = parseInt(hex.substring(2, 4), 16);
    const b = parseInt(hex.substring(4, 6), 16);
    const maxValue = 180;
    const invR = Math.min(255 - r, maxValue).toString(16).padStart(2, '0');
    const invG = Math.min(255 - g, maxValue).toString(16).padStart(2, '0');
    const invB = Math.min(255 - b, maxValue).toString(16).padStart(2, '0');
    return `#${invR}${invG}${invB}`;
  };

  const activeGradient = (isDarkMode && gradientDark) ? gradientDark : gradient;
  const colors = extractColors(activeGradient);

  // En wallpaper mode
  if (isWallpaperMode) {
    if (appId === 'fit') {
      return (
        <>
          <svg width="0" height="0" style={{ position: 'absolute', pointerEvents: 'none' }}>
            <defs>
              <linearGradient id={gradientId} x1="0%" y1="0%" x2="0%" y2="100%">
                {colors.map((color, index) => (
                  <stop key={index} offset={`${(index / (colors.length - 1)) * 100}%`} stopColor={color} />
                ))}
              </linearGradient>
            </defs>
          </svg>
          <Box component="span" sx={{ display: 'contents', '& svg path': { fill: `url(#${gradientId})`, stroke: `url(#${gradientId})` } }}>
            <BrainIcon />
          </Box>
        </>
      );
    }

    if (appId === 'fin') {
      return <Box component="span" sx={{ display: 'contents', '& svg path': { fill: '#FFFFFF', stroke: '#FFFFFF' } }}><BrainIcon /></Box>;
    }

    const darkestColor = colors[colors.length - 1] || '#1A1A1A';
    return <Box component="span" sx={{ display: 'contents', '& svg path': { fill: darkestColor, stroke: darkestColor } }}><BrainIcon /></Box>;
  }

  // Chat en dark mode usa color sólido más claro
  if (appId === 'chat' && isDarkMode) {
    const lightestColor = colors[0] || '#415B7E';
    return <Box component="span" sx={{ display: 'contents', '& svg path': { fill: lightestColor, stroke: lightestColor } }}><BrainIcon /></Box>;
  }

  // Fin usa colores invertidos
  const shouldInvertColors = appId === 'fin';
  const finalColors = shouldInvertColors ? colors.map(invertColor) : colors;

  return (
    <>
      <svg width="0" height="0" style={{ position: 'absolute', pointerEvents: 'none' }}>
        <defs>
          <linearGradient id={gradientId} x1="0%" y1="0%" x2="0%" y2="100%">
            {finalColors.map((color, index) => (
              <stop key={index} offset={`${(index / (finalColors.length - 1)) * 100}%`} stopColor={color} />
            ))}
          </linearGradient>
        </defs>
      </svg>
      <Box component="span" sx={{ display: 'contents', '& svg path': { fill: `url(#${gradientId})`, stroke: `url(#${gradientId})` } }}>
        <BrainIcon />
      </Box>
    </>
  );
};

// Componente para la previsualización de estilos con animaciones
const StylePreview = React.memo(({ style, selected, onClick }) => {
  const theme = useTheme();
  
  const handleSelection = (event) => {
    if (event) {
      event.stopPropagation();
      if (event.cancelable) {
        event.preventDefault();
      }
    }
    onClick(style);
  };
 
  const previewBackground = theme.palette.mode === 'dark' ? '#000000' : '#FFFFFF';
 
  const inputStyle = {
    borderRadius: '30px',
    backgroundColor: theme.palette.mode === 'dark' ? '#2C2C2E' : '#F2F2F7',
    height: '40px',
    width: '100%',
    marginBottom: 2,
    display: 'flex',
    alignItems: 'center',
    padding: '0 12px',
    border: `1px solid ${theme.palette.mode === 'dark' ? '#2C2C2E' : '#F2F2F7'}`
  };
 
  const renderStyleContent = () => {
    switch(style) {
      case INTERFACE_STYLES.APP:
        return (
          <Grid container spacing={1} sx={{ mt: 1 }}>
            {[1, 2, 3, 4].map(i => (
              <Grid item xs={3} key={i}>
                <Box sx={{
                  width: '100%',
                  aspectRatio: '1/1',
                  borderRadius: '12px',
                  backgroundColor: 'transparent',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  border: `1px solid ${theme.palette.mode === 'dark' ? '#333333' : '#E0E0E0'}`
                }}>
                  <Box sx={{
                    width: 8,
                    height: 8,
                    borderRadius: '50%',
                    backgroundColor: theme.palette.actualMode === 'wallpaper'
                      ? '#FFFFFF'
                      : theme.palette.mode === 'dark' ? '#0385FF' : '#007BFE'
                  }}/>
                </Box>
                <Typography variant="caption" sx={{ fontSize: '6px', mt: 0.5, display: 'block', textAlign: 'center' }}>
                  App
                </Typography>
              </Grid>
            ))}
          </Grid>
        );
 
      case INTERFACE_STYLES.ASSISTANT:
        return (
          <Box sx={{ 
            display: 'flex', 
            flexDirection: 'column',
            alignItems: 'center', 
            justifyContent: 'center', 
            gap: 1,
            mt: 2
          }}>
            <Box sx={{ display: 'flex', justifyContent: 'center', gap: 0.5 }}>
              {[1, 2, 3].map(i => (
                <Box key={i} sx={{
                  height: '14px',
                  borderRadius: '7px',
                  backgroundColor: 'transparent',
                  px: 1,
                  py: 0.25,
                  fontSize: '6px',
                  border: `1px solid ${theme.palette.mode === 'dark' ? '#333333' : '#E0E0E0'}`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  Suggestion {i}
                </Box>
              ))}
            </Box>
 
            <Box sx={{ display: 'flex', justifyContent: 'center', gap: 0.5 }}>
              {[4, 5].map(i => (
                <Box key={i} sx={{
                  height: '14px',
                  borderRadius: '7px',
                  backgroundColor: 'transparent',
                  px: 1,
                  py: 0.25,
                  fontSize: '6px',
                  border: `1px solid ${theme.palette.mode === 'dark' ? '#333333' : '#E0E0E0'}`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  Suggestion {i}
                </Box>
              ))}
            </Box>
          </Box>
        );
 
      default:
        return (
          <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
            <Box sx={{ flex: 1 }}>
              <Typography variant="caption" sx={{ fontSize: '6px', fontWeight: 'bold', mb: 0.5, display: 'block' }}>
                Explore
              </Typography>
              <Box sx={{
                p: 0.5,
                borderRadius: '10px',
                backgroundColor: 'transparent',
                mb: 0.5,
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                border: `1px solid ${theme.palette.mode === 'dark' ? '#333333' : '#E0E0E0'}`
              }}>
                <Box sx={{
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  backgroundColor: theme.palette.actualMode === 'wallpaper'
                    ? '#FFFFFF'
                    : theme.palette.mode === 'dark' ? '#0385FF' : '#007BFE'
                }}/>
                <Typography variant="caption" sx={{ fontSize: '6px' }}>Card</Typography>
              </Box>
            </Box>

            <Box sx={{ flex: 1 }}>
              <Typography variant="caption" sx={{ fontSize: '6px', fontWeight: 'bold', mb: 0.5, display: 'block' }}>
                Ask
              </Typography>
              <Box sx={{
                p: 0.5,
                borderRadius: '10px',
                backgroundColor: 'transparent',
                mb: 0.5,
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                border: `1px solid ${theme.palette.mode === 'dark' ? '#333333' : '#E0E0E0'}`
              }}>
                <Box sx={{
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  backgroundColor: theme.palette.actualMode === 'wallpaper'
                    ? '#FFFFFF'
                    : theme.palette.mode === 'dark' ? '#0385FF' : '#007BFE'
                }}/>
                <Typography variant="caption" sx={{ fontSize: '6px' }}>Card</Typography>
              </Box>
            </Box>
          </Box>
        );
    }
  };
 
  return (
    <motion.div
      whileHover={{ 
        scale: 1.02,
        transition: { duration: 0.2, ease: [0.25, 0.46, 0.45, 0.94] }
      }}
      whileTap={{ 
        scale: 0.98,
        transition: { duration: 0.1, ease: [0.25, 0.46, 0.45, 0.94] }
      }}
    >
      <Paper
        elevation={0}
        sx={{
          width: '100%',
          height: '180px',
          padding: 2,
          backgroundColor: previewBackground,
          borderRadius: '20px',
          cursor: 'pointer',
          border: selected
            ? theme.palette.actualMode === 'wallpaper'
              ? `2px solid #FFFFFF`
              : `2px solid ${theme.palette.mode === 'dark' ? '#0385FF' : '#007BFE'}`
            : `1px solid ${theme.palette.mode === 'dark' ? '#333333' : '#E0E0E0'}`,
          boxShadow: selected
            ? theme.palette.actualMode === 'wallpaper'
              ? '0 4px 20px rgba(255, 255, 255, 0.25)'
              : (theme.palette.mode === 'dark' ? '0 4px 20px rgba(3, 133, 255, 0.25)' : '0 4px 20px rgba(0, 123, 254, 0.25)')
            : 'none',
          transition: 'all 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
          overflow: 'hidden',
          position: 'relative',
          touchAction: 'auto',
          '&:hover': {
            boxShadow: selected
              ? theme.palette.actualMode === 'wallpaper'
                ? '0 6px 24px rgba(255, 255, 255, 0.35)'
                : (theme.palette.mode === 'dark' ? '0 6px 24px rgba(3, 133, 255, 0.35)' : '0 6px 24px rgba(0, 123, 254, 0.35)')
              : '0 2px 12px rgba(0, 0, 0, 0.08)',
          }
        }}
        onClick={(e) => {
          e.stopPropagation();
          handleSelection(e);
        }}
        onTouchEnd={(e) => {
          e.stopPropagation();
          handleSelection(e);
        }}
        onMouseDown={(e) => e.stopPropagation()}
        onTouchMove={(e) => e.stopPropagation()}
        onTouchStart={(e) => e.stopPropagation()}
      >
        <Box sx={inputStyle}>
          <Typography variant="caption" sx={{ opacity: 0.6 }}>Ask AGT</Typography>
        </Box>
 
        {renderStyleContent()}
 
        <Box 
          sx={{ 
            position: 'absolute',
            bottom: 10,
            right: 10,
            backgroundColor: theme.palette.mode === 'dark' ? 'rgba(0,0,0,0.5)' : 'rgba(255,255,255,0.7)',
            padding: '2px 8px',
            borderRadius: '4px',
          }}
        >
          <Typography variant="caption" sx={{ fontWeight: 'bold', textTransform: 'capitalize' }}>
            {style}
          </Typography>
        </Box>
 
        {selected && (
          <Box
            sx={{
              position: 'absolute',
              top: 10,
              right: 10,
              width: 24,
              height: 24,
              borderRadius: '50%',
              backgroundColor: theme.palette.actualMode === 'wallpaper'
                ? '#FFFFFF'
                : theme.palette.mode === 'dark' ? '#0385FF' : '#007BFE',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Check
              size={16}
              color={theme.palette.actualMode === 'wallpaper' ? '#000000' : '#FFFFFF'}
            />
          </Box>
        )}
      </Paper>
    </motion.div>
  );
});

StylePreview.propTypes = {
  style: PropTypes.string.isRequired,
  selected: PropTypes.bool.isRequired,
  onClick: PropTypes.func.isRequired,
};

// Fondo con blur animado
const BlurBackdrop = styled(motion.div)(({ theme }) => ({
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: theme.palette.mode === 'dark' 
    ? 'rgba(0, 0, 0, 0.3)'
    : 'rgba(255, 255, 255, 0.3)',
  backdropFilter: 'blur(10px)',
  WebkitBackdropFilter: 'blur(10px)',
  zIndex: 1400,
}));

// Contenedor móvil con animaciones mejoradas
const MobileContainer = styled(motion.div)(({ theme }) => {
  const isWallpaperMode = theme.palette.actualMode === 'wallpaper';

  return {
    position: 'fixed',
    bottom: 0,
    left: 0,
    width: '100%',
    borderTopLeftRadius: '16px',
    borderTopRightRadius: '16px',
    backgroundColor: isWallpaperMode
      ? 'rgba(128, 128, 128, 0.3)'
      : (theme.palette.mode === 'dark' ? '#1C1C1E' : '#FFFFFF'),
    backdropFilter: isWallpaperMode ? 'blur(10px)' : 'none',
    WebkitBackdropFilter: isWallpaperMode ? 'blur(10px)' : 'none',
    border: isWallpaperMode ? '1px solid rgba(128, 128, 128, 0.3)' : 'none',
    boxShadow: theme.palette.mode === 'dark'
      ? '0 -2px 20px rgba(0, 0, 0, 0.5)'
      : '0 -2px 20px rgba(0, 0, 0, 0.15)',
    zIndex: 1500,
    maxHeight: '85vh',
    overflowY: 'auto',
    display: 'flex',
    flexDirection: 'column',
    touchAction: 'auto',
  };
});

// Handle animado
const DragHandle = styled(motion.div)(({ theme }) => ({
  width: '36px',
  height: '5px',
  borderRadius: '3px',
  backgroundColor: theme.palette.mode === 'dark' ? '#8E8E93' : '#C7C7CC',
  opacity: 0.3,
  margin: '12px auto 8px',
  cursor: 'grab',
  transition: 'opacity 0.2s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
  '&:active': {
    cursor: 'grabbing',
    opacity: 0.5,
  }
}));

// Botones animados para móvil - SIN BRILLO
const MobileButton = styled(motion.button)(({ theme, variant }) => ({
  borderRadius: '12px',
  padding: '10px 16px',
  textTransform: 'capitalize',
  fontSize: '16px',
  fontWeight: 500,
  width: '100%',
  border: 'none',
  cursor: 'pointer',
  transition: 'all 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
  ...(variant === 'cancel' ? {
    color: theme.palette.actualMode === 'wallpaper' ? '#FFFFFF' : '#FF3B30',
    backgroundColor: 'transparent',
  } : {
    backgroundColor: theme.palette.actualMode === 'wallpaper'
      ? '#FFFFFF'
      : theme.palette.mode === 'dark' ? '#0385FF' : '#007BFE',
    color: theme.palette.actualMode === 'wallpaper' ? '#000000' : '#FFFFFF',
    boxShadow: 'none',
    '&:hover': {
      backgroundColor: theme.palette.actualMode === 'wallpaper'
        ? 'rgba(255, 255, 255, 0.85)'
        : theme.palette.mode === 'dark' ? '#0371DB' : '#0069D9',
      boxShadow: 'none',
    }
  })
}));

// Botones animados para desktop - SIN BRILLO
const DesktopButton = styled(motion.button)(({ theme, variant }) => ({
  borderRadius: '20px',
  minWidth: '100px',
  padding: '10px 24px',
  textTransform: 'capitalize',
  fontSize: '15px',
  fontWeight: 500,
  border: 'none',
  cursor: 'pointer',
  transition: 'all 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
  ...(variant === 'cancel' ? {
    color: theme.palette.actualMode === 'wallpaper' ? '#FFFFFF' : '#FF3B30',
    backgroundColor: 'transparent',
  } : {
    backgroundColor: theme.palette.actualMode === 'wallpaper'
      ? '#FFFFFF'
      : theme.palette.mode === 'dark' ? '#0385FF' : '#007BFE',
    color: theme.palette.actualMode === 'wallpaper' ? '#000000' : '#FFFFFF',
    boxShadow: 'none',
    '&:hover': {
      backgroundColor: theme.palette.actualMode === 'wallpaper'
        ? 'rgba(255, 255, 255, 0.85)'
        : theme.palette.mode === 'dark' ? '#0371DB' : '#0069D9',
      boxShadow: 'none',
    }
  })
}));

const AppCard = styled(Box)(({ theme, selected, disabled }) => ({
  display: 'flex',
  alignItems: 'center',
  padding: '12px 16px',
  borderRadius: '12px',
  cursor: disabled ? 'not-allowed' : 'pointer',
  backgroundColor: theme.palette.actualMode === 'wallpaper'
    ? 'rgba(128, 128, 128, 0.3)'
    : (theme.palette.mode === 'dark' ? '#2C2C2E' : '#F2F2F7'),
  backdropFilter: theme.palette.actualMode === 'wallpaper' ? 'blur(10px)' : 'none',
  WebkitBackdropFilter: theme.palette.actualMode === 'wallpaper' ? 'blur(10px)' : 'none',
  border: selected
    ? `2px solid ${theme.palette.actualMode === 'wallpaper' ? '#FFFFFF' : (theme.palette.mode === 'dark' ? '#0385FF' : '#007BFE')}`
    : theme.palette.actualMode === 'wallpaper'
      ? '1px solid rgba(128, 128, 128, 0.3)'
      : '1px solid transparent',
  opacity: disabled ? 0.5 : 1,
  transition: 'all 0.2s ease',
  '&:hover': disabled ? {} : {
    backgroundColor: theme.palette.actualMode === 'wallpaper'
      ? 'rgba(128, 128, 128, 0.4)'
      : (theme.palette.mode === 'dark' ? '#3A3A3C' : '#E5E5EA'),
    transform: 'translateY(-2px)'
  }
}));

// AppIconBox con borde gradiente (similar a Settings y Launchpad)
const AppIconBox = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'gradient' && prop !== 'gradientDark' && prop !== 'appId',
})(({ theme, gradient, gradientDark, appId }) => {
  const isWallpaperMode = theme.palette.actualMode === 'wallpaper';
  const isDarkMode = theme.palette.mode === 'dark';

  const extractColors = (gradientString) => {
    const colorRegex = /#[0-9A-Fa-f]{6}/g;
    return gradientString.match(colorRegex) || [];
  };

  const invertColor = (hex) => {
    hex = hex.replace('#', '');
    const r = parseInt(hex.substring(0, 2), 16);
    const g = parseInt(hex.substring(2, 4), 16);
    const b = parseInt(hex.substring(4, 6), 16);
    const maxValue = 180;
    const invR = Math.min(255 - r, maxValue).toString(16).padStart(2, '0');
    const invG = Math.min(255 - g, maxValue).toString(16).padStart(2, '0');
    const invB = Math.min(255 - b, maxValue).toString(16).padStart(2, '0');
    return `#${invR}${invG}${invB}`;
  };

  const baseGradient = gradient
    ? (isDarkMode && gradientDark ? gradientDark : gradient)
    : (isDarkMode ? 'linear-gradient(to bottom, #1C1C1E, #0F0F11)' : 'linear-gradient(to bottom, #34aadc, #007aff)');

  const isChatInDark = appId === 'chat' && isDarkMode && !isWallpaperMode;
  const chatLightColor = isChatInDark ? extractColors(gradientDark || gradient)[0] || '#415B7E' : null;

  const isFin = appId === 'fin' && !isWallpaperMode;
  let borderGradient = baseGradient;

  if (isFin) {
    const gradColors = extractColors(gradientDark || gradient);
    const invertedColors = gradColors.map(invertColor);
    borderGradient = `linear-gradient(to bottom, ${invertedColors.join(', ')})`;
  }

  const homeIconColor = !gradient && !isDarkMode ? '#007aff' : '#FFFFFF';

  return {
    position: 'relative',
    width: 40,
    height: 40,
    borderRadius: 10,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: '16px',
    background: isWallpaperMode
      ? 'rgba(255, 255, 255, 0.15)'
      : (isDarkMode ? '#1C1C1E' : '#FFFFFF'),
    backdropFilter: isWallpaperMode ? 'blur(10px)' : 'none',
    WebkitBackdropFilter: isWallpaperMode ? 'blur(10px)' : 'none',
    border: isWallpaperMode ? '1px solid rgba(255, 255, 255, 0.2)' : 'none',
    color: homeIconColor,
    flexShrink: 0,
    '&::before': isWallpaperMode ? {} : {
      content: '""',
      position: 'absolute',
      inset: 0,
      borderRadius: 10,
      padding: '2px',
      background: isChatInDark ? chatLightColor : borderGradient,
      WebkitMask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
      WebkitMaskComposite: 'xor',
      maskComposite: 'exclude',
      pointerEvents: 'none',
    },
    '& svg': {
      width: '60%',
      height: '60%',
    },
  };
});

// Variantes de animación
const backdropVariants = {
  hidden: { 
    opacity: 0,
    backdropFilter: 'blur(0px)'
  },
  visible: { 
    opacity: 1,
    backdropFilter: 'blur(10px)',
    transition: {
      duration: 0.3,
      ease: [0.25, 0.46, 0.45, 0.94]
    }
  },
  exit: {
    opacity: 0,
    backdropFilter: 'blur(0px)',
    transition: {
      duration: 0.2,
      ease: [0.25, 0.46, 0.45, 0.94]
    }
  }
};

const dialogVariants = {
  hidden: { 
    opacity: 0,
    scale: 0.95,
    y: 20
  },
  visible: { 
    opacity: 1,
    scale: 1,
    y: 0,
    transition: {
      duration: 0.4,
      ease: [0.25, 0.46, 0.45, 0.94]
    }
  },
  exit: {
    opacity: 0,
    scale: 0.95,
    y: 20,
    transition: {
      duration: 0.3,
      ease: [0.25, 0.46, 0.45, 0.94]
    }
  }
};

const mobileVariants = {
  hidden: { y: '100%' },
  visible: { 
    y: 0,
    transition: {
      type: 'spring',
      damping: 30,
      stiffness: 300,
      mass: 0.8
    }
  },
  exit: {
    y: '100%',
    transition: {
      duration: 0.3,
      ease: [0.25, 0.46, 0.45, 0.94]
    }
  }
};

const gridItemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: (index) => ({
    opacity: 1,
    y: 0,
    transition: {
      delay: index * 0.1,
      duration: 0.4,
      ease: [0.25, 0.46, 0.45, 0.94]
    }
  })
};

// Solo efecto tap, sin hover scale
const buttonTap = {
  scale: 0.98,
  transition: {
    duration: 0.1,
    ease: [0.25, 0.46, 0.45, 0.94]
  }
};

// Definición de apps disponibles
const availableApps = [
  {
    id: 'home',
    name: 'Home',
    description: 'Central hub',
    icon: Home,
    required: true
  },
  {
    id: 'chat',
    name: 'Chat',
    description: 'AI conversation',
    gradient: CHAT_GRADIENT_LIGHT,
    gradientDark: CHAT_GRADIENT_DARK,
    required: true
  },
  {
    id: 'ups',
    name: 'Ups',
    description: 'Business tools',
    gradient: UPS_GRADIENT_LIGHT,
    gradientDark: UPS_GRADIENT_DARK,
    required: false
  },
  {
    id: 'edu',
    name: 'Edu',
    description: 'Education',
    gradient: CHAT_GRADIENT_LIGHT,
    gradientDark: CHAT_GRADIENT_DARK,
    required: false
  },
  {
    id: 'fin',
    name: 'Fin',
    description: 'Finance tracker',
    gradient: JOB_GRADIENT_LIGHT,
    gradientDark: JOB_GRADIENT_DARK,
    required: false
  },
  {
    id: 'health',
    name: 'Health',
    description: 'Health tracking',
    gradient: HEALTH_GRADIENT_LIGHT,
    gradientDark: HEALTH_GRADIENT_DARK,
    required: false
  },
  {
    id: 'fit',
    name: 'Fit',
    description: 'Fitness',
    gradient: FIT_GRADIENT_LIGHT,
    gradientDark: FIT_GRADIENT_DARK,
    required: false
  },
];

const CustomizeModal = ({
  open,
  onClose,
  currentStyle,
  onStyleChange,
  selectedApps = ['home', 'chat'],
  userId
}) => {
  const [selectedStyle, setSelectedStyle] = useState(currentStyle);
  const [localSelectedApps, setLocalSelectedApps] = useState(selectedApps);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { t } = useTranslation();

  const contentRef = useRef(null);
  const [scrollPosition, setScrollPosition] = useState(0);

  // Sincronizar selectedApps cuando cambien las props
  useEffect(() => {
    setLocalSelectedApps(selectedApps);
  }, [selectedApps]);

  const handleStyleSelect = (style) => {
    if (isMobile && contentRef.current) {
      const currentScroll = contentRef.current.scrollTop;
      setScrollPosition(currentScroll);
    }
    setSelectedStyle(style);
  };

  const handleAppToggle = (appId) => {
    // No permitir desmarcar apps requeridas
    const app = availableApps.find(a => a.id === appId);
    if (app && app.required) {
      return;
    }

    setLocalSelectedApps(prev => {
      if (prev.includes(appId)) {
        return prev.filter(id => id !== appId);
      } else {
        return [...prev, appId];
      }
    });
  };

  useEffect(() => {
    if (isMobile && contentRef.current && scrollPosition > 0) {
      setTimeout(() => {
        contentRef.current.scrollTop = scrollPosition;
      }, 50);
    }
  }, [selectedStyle, isMobile, scrollPosition]);

  const handleApply = async () => {

    // Aplicar el cambio de estilo
    onStyleChange(selectedStyle);

    // Guardar apps seleccionadas si estamos en modo APP y hay cambios
    if (userId && selectedStyle === INTERFACE_STYLES.APP &&
        JSON.stringify(localSelectedApps.sort()) !== JSON.stringify(selectedApps.sort())) {
      try {

        // Determinar defaultApp (el primero de la lista que no sea home ni chat)
        const defaultApp = localSelectedApps.find(app => app !== 'home' && app !== 'chat') || localSelectedApps[0] || 'home';

        await saveUserSelectedApps(userId, localSelectedApps, defaultApp);


        // Disparar evento para actualizar el estado global
        const event = new CustomEvent('appsConfigurationChanged', {
          detail: {
            selectedApps: localSelectedApps,
            showUpsInNav: localSelectedApps.includes('ups'),
            showEduInNav: localSelectedApps.includes('edu'),
            showFinInNav: localSelectedApps.includes('fin'),
            showHealthInNav: localSelectedApps.includes('health'),
            showFitInNav: localSelectedApps.includes('fit')
          }
        });
        window.dispatchEvent(event);
      } catch (error) {
        console.error('[CustomizeModal] Error saving selected apps:', error);
      }
    }

    onClose();
  };

  const handleDragEnd = (_, info) => {
    if (info.offset.y > window.innerHeight * 0.25) {
      onClose();
    }
  };

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  if (isMobile) {
    return (
      <>
        <AnimatePresence>
          {open && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: 'transparent',
                zIndex: 1400,
              }}
              onClick={handleBackdropClick}
            />
          )}
        </AnimatePresence>

        <Modal
          open={open}
          onClose={onClose}
          closeAfterTransition
          disableAutoFocus
          disableEnforceFocus
          disableRestoreFocus
          disableScrollLock={false}
          keepMounted
          BackdropProps={{ invisible: true }}
        >
          <Box>
            <AnimatePresence>
              {open && (
                <MobileContainer
                variants={mobileVariants}
                initial="hidden"
                animate="visible"
                exit="exit"
                drag="y"
                dragConstraints={{ top: 0, bottom: window.innerHeight }}
                dragElastic={0.2}
                onDragEnd={handleDragEnd}
                dragDirectionLock
                dragMomentum={false}
                style={{ touchAction: 'auto' }}
                onClick={(e) => e.stopPropagation()}
              >
                <Box 
                  sx={{ 
                    width: '100%', 
                    touchAction: 'none',
                    cursor: 'grab',
                    '&:active': { cursor: 'grabbing' }
                  }}
                  onClick={(e) => e.stopPropagation()}
                >
                  <DragHandle 
                    whileHover={{ opacity: 0.5 }}
                    whileTap={{ opacity: 0.7 }}
                  />
                  
                  <Box sx={{ 
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    position: 'relative',
                    padding: '0 16px 10px'
                  }}>
                    <Typography 
                      variant="subtitle1" 
                      sx={{ 
                        fontWeight: 700,
                        fontSize: '24px',
                        color: theme.palette.mode === 'dark' ? '#FFFFFF' : '#000000'
                      }}
                    >
                      {t('homeInput.customize_title')}
                    </Typography>
                    <motion.div
                      whileHover={{ scale: 1.1 }}
                      whileTap={{ scale: 0.9 }}
                      style={{ position: 'absolute', right: 8 }}
                    >
                      <IconButton 
                        onClick={(e) => {
                          e.stopPropagation();
                          onClose();
                        }}
                        sx={{ 
                          color: theme.palette.mode === 'dark' ? '#8E8E93' : '#8E8E93'
                        }}
                      >
                        <ChevronDown size={20} />
                      </IconButton>
                    </motion.div>
                  </Box>
                </Box>
                
                <DialogContent 
                  ref={contentRef} 
                  sx={{ 
                    flex: '1 1 auto',
                    overflowY: 'auto',
                    pb: 2,
                    '&:focus': { outline: 'none' },
                    touchAction: 'auto'
                  }}
                  onClick={(e) => e.stopPropagation()}
                >
                  <Typography variant="body1" sx={{ 
                    mb: 3, 
                    textAlign: 'center',
                    color: theme.palette.mode === 'dark' ? '#FFFFFF' : '#000000'
                  }}>
                    {t('homeInput.customize_description')}
                  </Typography>
                  
                  <Grid
                    container
                    spacing={2}
                    sx={{ mb: 2 }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    {Object.values(INTERFACE_STYLES).map((style, index) => (
                      <Grid
                        item
                        xs={12}
                        key={style}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <motion.div
                          custom={index}
                          variants={gridItemVariants}
                          initial="hidden"
                          animate="visible"
                        >
                          <StylePreview
                            style={style}
                            selected={selectedStyle === style}
                            onClick={handleStyleSelect}
                          />
                        </motion.div>
                      </Grid>
                    ))}
                  </Grid>

                  {/* Sección de selección de apps - Móvil - Solo para estilo APP */}
                  {selectedStyle === INTERFACE_STYLES.APP && (
                    <Box sx={{ mt: 3, mb: 2 }}>
                      <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                        Select Apps
                      </Typography>
                      <Typography variant="body2" sx={{ mb: 2, opacity: 0.7 }}>
                        Choose which apps to display in App Layout
                      </Typography>
                      <Grid container spacing={2}>
                        {availableApps.map((app) => {
                          const isSelected = localSelectedApps.includes(app.id);
                          const isDisabled = app.required;

                          return (
                            <Grid item xs={12} key={app.id}>
                              <AppCard
                                selected={isSelected}
                                disabled={isDisabled}
                                onClick={() => !isDisabled && handleAppToggle(app.id)}
                              >
                                <AppIconBox
                                  gradient={app.gradient}
                                  gradientDark={app.gradientDark}
                                  appId={app.id}
                                >
                                  {app.gradient ? (
                                    <BrainIconWithGradient
                                      gradient={app.gradient}
                                      gradientDark={app.gradientDark}
                                      appId={app.id}
                                    />
                                  ) : app.icon ? (
                                    <app.icon size={20} />
                                  ) : null}
                                </AppIconBox>
                                <Box sx={{ flex: 1 }}>
                                  <Typography variant="body1" sx={{ fontWeight: 600 }}>
                                    {app.name}
                                  </Typography>
                                  <Typography variant="caption" sx={{ opacity: 0.6 }}>
                                    {app.description}
                                  </Typography>
                                </Box>
                                <Checkbox
                                  checked={isSelected}
                                  disabled={isDisabled}
                                  onChange={() => !isDisabled && handleAppToggle(app.id)}
                                  onClick={(e) => e.stopPropagation()}
                                  sx={{
                                    color: theme.palette.actualMode === 'wallpaper'
                                      ? '#FFFFFF'
                                      : (theme.palette.mode === 'dark' ? '#0385FF' : '#007BFE'),
                                    '&.Mui-checked': {
                                      color: theme.palette.actualMode === 'wallpaper'
                                        ? '#FFFFFF'
                                        : (theme.palette.mode === 'dark' ? '#0385FF' : '#007BFE')
                                    }
                                  }}
                                />
                              </AppCard>
                            </Grid>
                          );
                        })}
                      </Grid>
                    </Box>
                  )}
                </DialogContent>
                
                <Box sx={{
                  position: 'sticky',
                  bottom: 0,
                  width: '100%',
                  backgroundColor: theme.palette.actualMode === 'wallpaper'
                    ? 'transparent'
                    : (theme.palette.mode === 'dark' ? '#1C1C1E' : '#FFFFFF'),
                  backdropFilter: 'none',
                  WebkitBackdropFilter: 'none',
                  borderTop: `1px solid ${theme.palette.actualMode === 'wallpaper' ? 'rgba(128, 128, 128, 0.3)' : (theme.palette.mode === 'dark' ? '#2C2C2E' : '#E5E5EA')}`,
                  pt: 2,
                  pb: 4,
                  px: 3,
                  display: 'flex',
                  justifyContent: 'space-between',
                  gap: 2,
                  zIndex: 10,
                  touchAction: 'auto'
                }}
                onClick={(e) => e.stopPropagation()}
                >
                  <MobileButton 
                    variant="cancel"
                    onClick={(e) => {
                      e.stopPropagation();
                      onClose();
                    }}
                    whileTap={buttonTap}
                  >
                    {t('homeInput.customize_cancel')}
                  </MobileButton>
                  <MobileButton 
                    onClick={(e) => {
                      e.stopPropagation();
                      handleApply();
                    }}
                    whileTap={buttonTap}
                  >
                    {t('homeInput.customize_apply')}
                  </MobileButton>
                </Box>
              </MobileContainer>
            )}
            </AnimatePresence>
          </Box>
        </Modal>
      </>
    );
  } else {
    return (
      <>
        <AnimatePresence>
          {open && (
            <BlurBackdrop 
              variants={backdropVariants}
              initial="hidden"
              animate="visible"
              exit="exit"
              onClick={handleBackdropClick}
            />
          )}
        </AnimatePresence>
        
        <Dialog 
          open={open} 
          onClose={onClose}
          maxWidth="md"
          fullWidth
          BackdropProps={{ invisible: true }}
          PaperProps={{
            component: motion.div,
            variants: dialogVariants,
            initial: "hidden",
            animate: "visible",
            exit: "exit",
            sx: {
              borderRadius: '20px',
              padding: 2,
              backgroundColor: theme.palette.actualMode === 'wallpaper'
                ? 'rgba(55, 55, 55, 0.1)'
                : (theme.palette.mode === 'dark' ? '#1C1C1E' : '#FFFFFF'),
              backdropFilter: theme.palette.actualMode === 'wallpaper' ? 'blur(10px)' : 'none',
              WebkitBackdropFilter: theme.palette.actualMode === 'wallpaper' ? 'blur(10px)' : 'none',
              border: theme.palette.actualMode === 'wallpaper' ? '1px solid rgba(55, 55, 55, 0.1)' : 'none',
              boxShadow: theme.palette.mode === 'dark'
                ? '0 10px 40px rgba(0, 0, 0, 0.4)'
                : '0 10px 40px rgba(0, 0, 0, 0.1)',
            },
            onClick: (e) => e.stopPropagation()
          }}
        >
          <DialogTitle sx={{ 
            textAlign: 'center', 
            fontWeight: 700,
            fontSize: '32px',
            mb: 2
          }}
          onClick={(e) => e.stopPropagation()}
          >
            {t('homeInput.customize_title')}
          </DialogTitle>
          <DialogContent onClick={(e) => e.stopPropagation()}>
            <Typography variant="body1" sx={{ mb: 3, textAlign: 'center' }}>
              {t('homeInput.customize_description')}
            </Typography>
            
            <Grid container spacing={3} onClick={(e) => e.stopPropagation()}>
              {Object.values(INTERFACE_STYLES).map((style, index) => (
                <Grid item xs={12} sm={4} key={style} onClick={(e) => e.stopPropagation()}>
                  <motion.div
                    custom={index}
                    variants={gridItemVariants}
                    initial="hidden"
                    animate="visible"
                  >
                    <StylePreview
                      style={style}
                      selected={selectedStyle === style}
                      onClick={handleStyleSelect}
                    />
                  </motion.div>
                </Grid>
              ))}
            </Grid>

            {/* Sección de selección de apps - Solo para estilo APP */}
            {selectedStyle === INTERFACE_STYLES.APP && (
              <Box sx={{ mt: 4 }}>
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                  Select Apps
                </Typography>
                <Typography variant="body2" sx={{ mb: 2, opacity: 0.7 }}>
                  Choose which apps to display in App Layout
                </Typography>
                <Grid container spacing={2}>
                  {availableApps.map((app) => {
                    const isSelected = localSelectedApps.includes(app.id);
                    const isDisabled = app.required;

                    return (
                      <Grid item xs={12} sm={6} key={app.id}>
                        <AppCard
                          selected={isSelected}
                          disabled={isDisabled}
                          onClick={() => !isDisabled && handleAppToggle(app.id)}
                        >
                          <AppIconBox
                            gradient={app.gradient}
                            gradientDark={app.gradientDark}
                            appId={app.id}
                          >
                            {app.gradient ? (
                              <BrainIconWithGradient
                                gradient={app.gradient}
                                gradientDark={app.gradientDark}
                                appId={app.id}
                              />
                            ) : app.icon ? (
                              <app.icon size={20} />
                            ) : null}
                          </AppIconBox>
                          <Box sx={{ flex: 1 }}>
                            <Typography variant="body1" sx={{ fontWeight: 600 }}>
                              {app.name}
                            </Typography>
                            <Typography variant="caption" sx={{ opacity: 0.6 }}>
                              {app.description}
                            </Typography>
                          </Box>
                          <Checkbox
                            checked={isSelected}
                            disabled={isDisabled}
                            onChange={() => !isDisabled && handleAppToggle(app.id)}
                            onClick={(e) => e.stopPropagation()}
                            sx={{
                              color: theme.palette.actualMode === 'wallpaper'
                                ? '#FFFFFF'
                                : (theme.palette.mode === 'dark' ? '#0385FF' : '#007BFE'),
                              '&.Mui-checked': {
                                color: theme.palette.actualMode === 'wallpaper'
                                  ? '#FFFFFF'
                                  : (theme.palette.mode === 'dark' ? '#0385FF' : '#007BFE')
                              }
                            }}
                          />
                        </AppCard>
                      </Grid>
                    );
                  })}
                </Grid>
              </Box>
            )}
          </DialogContent>
          <DialogActions sx={{ justifyContent: 'center', mt: 2 }} onClick={(e) => e.stopPropagation()}>
            <DesktopButton 
              variant="cancel"
              onClick={(e) => {
                e.stopPropagation();
                onClose();
              }}
              whileTap={buttonTap}
            >
              {t('homeInput.customize_cancel')}
            </DesktopButton>
            <DesktopButton 
              onClick={(e) => {
                e.stopPropagation();
                handleApply();
              }}
              whileTap={buttonTap}
            >
              {t('homeInput.customize_apply')}
            </DesktopButton>
          </DialogActions>
        </Dialog>
      </>
    );
  }
};

CustomizeModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  currentStyle: PropTypes.string.isRequired,
  onStyleChange: PropTypes.func.isRequired,
  selectedApps: PropTypes.arrayOf(PropTypes.string),
  userId: PropTypes.string,
};

export default CustomizeModal;